import React from 'react'

import { UpdateOrderInput } from 'src/graphql'
import { useActions, useMutations } from 'src/hooks'
import { Transition } from 'src/chrome'
import { ReplaceOrderCardRoute } from 'src/orders/routes/ReplaceOrderCardRoute'
import { AddCardToGiftRoute } from 'src/orders/routes/AddCardToGiftRoute'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'
import { orderCostWithUpgrades } from 'src/helpers/payments'
import CardPicker from 'src/card_picker'
import {
  buildUpdateOrderInput,
  OrderWithPartialLines,
} from 'src/redux/reducers/orders'

type Props = {
  subroute: ReplaceOrderCardRoute | AddCardToGiftRoute | AddOrderCardRoute
  order: OrderWithPartialLines
  onReload: () => Promise<void>
  onClose: () => void
  isLoading: boolean
}

const AddCard: React.FC<Props> = props => {
  const { order, subroute, onReload, onClose, isLoading } = props

  const actions = useActions()
  const mutations = useMutations()
  const cardCount = order.lines.length ?? 0

  const { headerText, applyChanges, saveButtonLabel } = (() => {
    switch (subroute.path) {
      case AddCardToGiftRoute.path:
        return {
          headerText: 'Adding card to gift',
          applyChanges: (orderInput: UpdateOrderInput, cardId: string) => ({
            ...orderInput,
            lines: (orderInput.lines || []).map((line, index) => ({
              ...line,
              card: index === 0 ? cardId : line.card,
            })),
          }),
          saveButtonLabel: 'Add to gift',
        }
      case AddOrderCardRoute.path:
        return {
          headerText: 'Adding card to order',
          applyChanges: (orderInput: UpdateOrderInput, cardId: string) => ({
            ...orderInput,
            lines: (orderInput.lines || []).concat([
              {
                card: cardId,
                giftVariation: undefined,
                sendDelay: null,
              },
            ]),
          }),
          saveButtonLabel: 'Add to order',
        }
      case ReplaceOrderCardRoute.path:
        return {
          headerText: 'Replacing card',
          applyChanges: (orderInput: UpdateOrderInput, newCardId: string) => ({
            ...orderInput,
            lines: (orderInput.lines || []).map((line, index) => ({
              ...line,
              card: line.card === subroute.cardId ? newCardId : line.card,
            })),
          }),
          saveButtonLabel: 'Replace',
        }
    }
  })()

  const handleAdd = async (cardId: string) => {
    const orderInput = buildUpdateOrderInput(order)
    await mutations.updateOrder({ order: applyChanges(orderInput, cardId) })
    await onReload()
  }

  return (
    <>
      {isLoading && <Transition message={'Loading your order...'} />}
      <CardPicker
        context={{
          headerText,
          footerLabel: card => (
            <div style={{ display: 'flex' }}>
              <b>
                {cardCount} card{cardCount > 1 ? 's' : ''}
              </b>
              <div style={{ marginLeft: '1rem' }}>
                Total: {orderCostWithUpgrades(order, card)}
              </div>
            </div>
          ),
          lines: order.lines,
        }}
        saveButtonLabel={saveButtonLabel}
        onClose={onClose}
        onAfterSave={handleAdd}
        catalogSubroute={subroute.subroute}
        onCatalogNavigate={catalogSubroute =>
          actions.openOrder(order.id, {
            ...subroute,
            subroute: catalogSubroute,
          })
        }
      />
    </>
  )
}

export default AddCard
